import { motion } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';
import { getTodayCO, getNowCO } from '@/lib/dateUtils';

interface Props {
  weekStart: Date;
  appointments: AppointmentRow[];
  onSelectAppointment: (appt: AppointmentRow) => void;
}

const HOUR_START = 8;
const HOUR_END = 20;
const SLOT_HEIGHT = 56;

function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getStatusColor(status: string) {
  if (status === 'confirmed') return { bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.35)', text: '#34d399' };
  if (status === 'cancelled') return { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#f87171' };
  return { bg: 'rgba(201,168,76,0.15)', border: 'rgba(201,168,76,0.35)', text: '#c9a84c' };
}

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function CalendarWeekView({ weekStart, appointments, onSelectAppointment }: Props) {
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const todayStr = getTodayCO();
  const { hours: nowH, minutes: nowM } = getNowCO();
  const currentMinutes = nowH * 60 + nowM;

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Day headers */}
      <div
        className="flex border-b"
        style={{ borderColor: theme.colors.border }}
      >
        <div className="w-14 flex-shrink-0" />
        {days.map((day) => {
          const ds = toDateStr(day);
          const isToday = ds === todayStr;
          const count = appointments.filter((a) => a.appointment_date === ds).length;
          return (
            <div
              key={ds}
              className="flex-1 flex flex-col items-center py-3 border-l"
              style={{ borderColor: theme.colors.border }}
            >
              <p className="text-white/40 text-xs uppercase tracking-wider">{DAY_NAMES[day.getDay()]}</p>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full mt-1 text-sm font-bold ${
                  isToday ? 'text-[#0a0a0a]' : 'text-white/70'
                }`}
                style={{ backgroundColor: isToday ? theme.colors.primary : 'transparent' }}
              >
                {day.getDate()}
              </div>
              {count > 0 && (
                <div
                  className="mt-1 px-1.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
                >
                  {count}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex">
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0">
            {hours.map((hour) => (
              <div
                key={hour}
                className="flex items-start justify-end pr-2 text-white/25 text-xs font-mono"
                style={{ height: SLOT_HEIGHT }}
              >
                <span className="-mt-2">{hour}:00</span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => {
            const ds = toDateStr(day);
            const isToday = ds === todayStr;
            const dayAppts = appointments.filter((a) => a.appointment_date === ds);

            return (
              <div
                key={ds}
                className="flex-1 relative border-l"
                style={{
                  borderColor: theme.colors.border,
                  height: SLOT_HEIGHT * hours.length,
                  backgroundColor: isToday ? 'rgba(201,168,76,0.02)' : 'transparent',
                }}
              >
                {/* Hour lines */}
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="absolute left-0 right-0 border-t"
                    style={{
                      top: (hour - HOUR_START) * SLOT_HEIGHT,
                      borderColor: theme.colors.border,
                    }}
                  />
                ))}

                {/* Current time */}
                {isToday && currentMinutes >= HOUR_START * 60 && currentMinutes <= HOUR_END * 60 && (
                  <div
                    className="absolute left-0 right-0 z-10 h-px"
                    style={{
                      top: ((currentMinutes - HOUR_START * 60) / 60) * SLOT_HEIGHT,
                      backgroundColor: theme.colors.primary,
                    }}
                  />
                )}

                {/* Appointments */}
                {dayAppts.map((appt, idx) => {
                  const startMin = timeToMinutes(appt.start_time);
                  const endMin = timeToMinutes(appt.end_time);
                  const top = ((startMin - HOUR_START * 60) / 60) * SLOT_HEIGHT;
                  const height = Math.max(((endMin - startMin) / 60) * SLOT_HEIGHT, 28);
                  const colors = getStatusColor(appt.status);

                  return (
                    <motion.button
                      key={appt.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => onSelectAppointment(appt)}
                      className="absolute left-1 right-1 rounded-md px-1.5 py-1 text-left cursor-pointer transition-all hover:brightness-125 hover:z-20"
                      style={{
                        top,
                        height,
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`,
                        zIndex: 5,
                      }}
                    >
                      <p
                        className="text-xs font-semibold truncate leading-tight"
                        style={{ color: colors.text, fontSize: '10px' }}
                      >
                        {appt.start_time.slice(0, 5)} {appt.client_name}
                      </p>
                      {height > 40 && (
                        <p className="text-white/40 truncate leading-tight" style={{ fontSize: '10px' }}>
                          {appt.services?.name ?? '—'}
                        </p>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
